/**
 * useOnboardingStatus Hook
 *
 * Checks whether the current user has completed onboarding by calling the
 * `get-onboarding` CF Worker endpoint. Exposes a mutation to mark
 * onboarding as complete via `update-onboarding`.
 *
 * Behaviour:
 * - Returns `{ isOnboarded: true }` for anonymous users (no session)
 * - Queries profiles table for authenticated users (via worker)
 * - `completeOnboarding()` sets is_onboarded = true via worker
 * - Updates local cache optimistically via React Query invalidation
 *
 * DTO migration (Wave 4A): both the GET + POST responses are parsed through
 * the shared `OnboardingStatusDto` / `UpdateOnboardingResultDto` via
 * `fetchJsonValidated`. Schema drift now surfaces as a runtime error rather
 * than silently defaulting to `false`.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Platform } from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import {
  OnboardingStatusDto,
  UpdateOnboardingResultDto,
} from '@dramadetector/shared';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { captureEvent } from '../lib/analytics';

/** Stable noop for anonymous users — avoids new closure on every render. */
const NOOP_COMPLETE_ONBOARDING = async (): Promise<void> => {};

/** Query key for onboarding status */
const ONBOARDING_QUERY_KEY = ['onboarding-status'] as const;

const ONBOARDING_STORAGE_KEY = 'dd_onboarding_completed';

function getLocalOnboardingFlag(): boolean {
  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
  }
  return false;
}

function setLocalOnboardingFlag(): void {
  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
  }
}

interface OnboardingStatus {
  /** Whether the user has completed onboarding */
  isOnboarded: boolean;
  /** Whether the status is still loading from the worker */
  isLoading: boolean;
  /** Mark onboarding as complete via the worker */
  completeOnboarding: () => Promise<void>;
}

export function useOnboardingStatus(): OnboardingStatus {
  const { accessToken, user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch onboarding status from profiles table via the worker
  const { data: isOnboarded, isLoading } = useQuery({
    queryKey: [...ONBOARDING_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user?.id) return true; // Anonymous = skip onboarding

      // Local fallback: if we previously completed onboarding but the server
      // update failed, don't pester the user again
      if (getLocalOnboardingFlag()) return true;

      try {
        const dto = await fetchJsonValidated(
          OnboardingStatusDto,
          getEdgeFunctionUrl('get-onboarding'),
        );
        return dto.is_onboarded ?? false;
      } catch {
        // Auth/network/validation errors → skip onboarding (don't block the user).
        console.warn('[useOnboardingStatus] get-onboarding request failed');
        return true;
      }
    },
    // Phase 2 (NocoDB #3844): skip for anon BA users. Strict `auth` endpoint
    // correctly 401s anon callers; we return the "skip onboarding" baseline
    // via the early-return below instead of wasting a round trip.
    enabled: !!accessToken && !!user?.id && !user.isAnonymous,
    staleTime: 5 * 60 * 1000, // 5 minutes — rarely changes
    gcTime: 30 * 60 * 1000,
  });

  // Mutation to mark onboarding as complete via the worker
  const mutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) return;

      // Set local flag immediately — even if the server update fails,
      // the user should not see onboarding again this session
      setLocalOnboardingFlag();

      try {
        await fetchJsonValidated(
          UpdateOnboardingResultDto,
          getEdgeFunctionUrl('update-onboarding'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_onboarded: true }),
          },
        );
      } catch (err) {
        console.error('[useOnboardingStatus] Failed to update is_onboarded:', err);
        // Don't throw — local flag already set, user won't see onboarding again.
        // The server update will be retried on next login if profile still has is_onboarded=false.
        return;
      }

      captureEvent('onboarding_completed', {
        user_id: user.id,
      });
    },
    onSuccess: () => {
      // Invalidate the query so it refetches with is_onboarded = true
      queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
    },
  });

  const completeOnboarding = async () => {
    await mutation.mutateAsync();
  };

  // Anonymous users or missing session: skip onboarding.
  // Phase 2 (NocoDB #3844): anon BA users have accessToken + user.id but
  // should still skip — same UX as pre-Phase-2 anon.
  if (!accessToken || !user?.id || user.isAnonymous) {
    return {
      isOnboarded: true,
      isLoading: false,
      completeOnboarding: NOOP_COMPLETE_ONBOARDING,
    };
  }

  return {
    isOnboarded: isOnboarded ?? false, // undefined (loading) defaults to false; queryFn errors return true (skip)
    isLoading,
    completeOnboarding,
  };
}
